
import { useEffect, useState } from 'react';
import { sitemapGenerator } from '@/utils/seo/sitemapGenerator';

const SitemapXml = () => {
  const [sitemapContent, setSitemapContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const generateSitemap = async () => {
      try {
        console.log('Generating sitemap...');
        const sitemap = await sitemapGenerator.generateSitemap();
        setSitemapContent(sitemap);
        
        // Set document title for SEO
        document.title = 'Sitemap - ReCut App';
        
        // Add XML content type meta tag
        const existingMeta = document.querySelector('meta[http-equiv="Content-Type"]');
        if (existingMeta) {
          existingMeta.remove();
        }
        
        const meta = document.createElement('meta');
        meta.setAttribute('http-equiv', 'Content-Type');
        meta.setAttribute('content', 'application/xml; charset=utf-8');
        document.head.appendChild(meta);
        
      } catch (error) {
        console.error('Error generating sitemap:', error);
        setError('Failed to generate sitemap');
        
        // Fallback to basic sitemap
        const basicSitemap = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
  <url>
    <loc>https://app.recut.art/</loc>
    <lastmod>${new Date().toISOString().split('T')[0]}</lastmod>
    <changefreq>daily</changefreq>
    <priority>1.0</priority>
  </url>
</urlset>`;
        setSitemapContent(basicSitemap);
      } finally {
        setIsLoading(false);
      }
    };
    
    generateSitemap();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Generating sitemap...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center text-red-600">
          <p>Error: {error}</p>
          <p className="mt-2 text-sm">Please try refreshing the page.</p>
        </div>
      </div>
    );
  }

  // Render the XML content in a pre-formatted way
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900">XML Sitemap</h1>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  const blob = new Blob([sitemapContent], { type: 'application/xml' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'sitemap.xml';
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Download XML
              </button>
              <button
                onClick={() => navigator.clipboard.writeText(sitemapContent)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
              >
                Copy to Clipboard
              </button>
            </div>
          </div>
          
          <div className="bg-gray-900 rounded-lg p-4 overflow-auto">
            <pre className="text-green-400 text-sm font-mono whitespace-pre-wrap break-all">
              {sitemapContent}
            </pre>
          </div>
          
          <div className="mt-4 text-sm text-gray-600">
            <p>This sitemap contains {sitemapContent.split('<url>').length - 1} URLs and is dynamically generated from the database.</p>
            <p className="mt-1">Last generated: {new Date().toLocaleString()}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SitemapXml;
